import React from 'react';
import { useSelect, UseSelectState } from 'downshift';
import styled, { css, useTheme } from 'styled-components/macro';
import { Text } from '../../typography';
import { SPACINGS } from '../../utils/style';
import { IconChevron } from '../../icons';
import { SelectProps } from './Select';

const SelectContainer = styled.div`
  width: ${(p) => p.theme.dimensions.inputWidth};
`;

type SelectStyledType = {
  isMenuOpen: boolean;
  disabled: boolean;
  isFocused: boolean;
  error: boolean;
};

const SelectWrapperStyled = styled.div<SelectStyledType>`
  position: relative;
  height: ${SPACINGS[10]};
  width: 100%;

  border: 1px solid
    ${(p) => (p.error ? p.theme.colors.danger : p.theme.colors.secondary80w)};
  border-radius: ${(p) => (p.isMenuOpen ? '10px 10px 0 0' : '10px')};
  background-color: ${(p) =>
    p.disabled ? p.theme.colors.secondary98w : 'transparent'};
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'auto')};

  ${(p) =>
    (p.isMenuOpen || p.isFocused) &&
    css`
      box-shadow: 0 0 0 2px ${p.theme.colors.primary};
      border: 1px solid
        ${p.error ? p.theme.colors.danger : p.theme.colors.secondary80w};
    `}

  &:hover {
    border: 1px solid ${(p) => p.theme.colors.primary};
  }

  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
`;

const SelectStyled = styled.button<SelectStyledType>`
  width: 100%;
  height: 100%;
  background-color: transparent;
  border: none;
  outline: none;
  padding: 0;
  margin: 0;

  &:focus {
    outline: none;
  }
`;

const SelectItemStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: ${SPACINGS[10]};
  margin: 0 ${SPACINGS[5]};
`;

const MenuWrapper = styled.ul`
  margin: 0;
  padding: 0;
  border-top: 0;
  outline: none;
  position: absolute;
  right: -1px;
  left: -1px;
  z-index: ${(p) => p.theme.zIndexes.overlay};
`;

const MenuStyled = styled.div`
  outline: none;
  padding: 0;
  margin: 0;
  margin-top: 3px;
  width: 100%;
  max-height: 10em;
  overflow: auto;
  background-color: ${(p) => p.theme.colors.white};
  border: 1px solid ${(p) => p.theme.colors.black};
  border-radius: 0 0
    ${(p) =>
      `${p.theme.dimensions.borderRadius} ${p.theme.dimensions.borderRadius}`};
  z-index: ${(p) => p.theme.zIndexes.overlay};
  box-shadow: 0 0 0 2px ${(p) => p.theme.colors.primary};

  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
`;

const ItemStyled = styled.li<{ bold: boolean; highlighted: boolean }>`
  list-style: none;
  padding: ${SPACINGS[2]} ${SPACINGS[5]};
  background-color: ${(p) =>
    p.highlighted ? p.theme.colors.primary95w : p.theme.colors.white};

  & span {
    font-weight: ${(p) => (p.bold ? 'bold' : 'unset')};
  }
`;

function Select<Item>(props: SelectProps<Item>) {
  const {
    id,
    label,
    items,
    initialSelectedItem,
    rowComponent,
    itemToString,
    onItemChange,
    disabled,
    style,
    clearItemAfterChange,
    placeholder = 'Select...',
    error,
    name,
    testid,
  } = props;

  const theme = useTheme();
  const newId = id ?? name;

  // to handle controlled and uncontrolled selectedItem
  const selectProps: Partial<UseSelectState<Item>> = {};
  if (typeof props.selectedItem !== 'undefined') {
    selectProps.selectedItem = props.selectedItem;
  }

  const [isFocused, setIsFocused] = React.useState(false);

  const {
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
  } = useSelect({
    id: newId,
    items,
    itemToString,
    initialSelectedItem,
    stateReducer: (_, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      if (
        changes.selectedItem &&
        (type === useSelect.stateChangeTypes.ItemClick ||
          type === useSelect.stateChangeTypes.MenuKeyDownEnter)
      ) {
        setTimeout(() => onItemChange(changes.selectedItem));

        if (clearItemAfterChange) {
          return {} as UseSelectState<Item>;
        }
      }
      return changes;
    },
    ...selectProps,
  });

  return (
    <SelectContainer style={style}>
      {label && (
        <>
          {typeof label === 'string' ? (
            <Text type="label" {...getLabelProps()}>
              {label}
            </Text>
          ) : (
            label(getLabelProps())
          )}
        </>
      )}
      <SelectWrapperStyled
        isMenuOpen={isOpen}
        disabled={Boolean(disabled)}
        isFocused={isFocused}
        error={Boolean(error)}
      >
        <SelectStyled
          data-testid={`${testid}-button`}
          tabIndex="0"
          type="button"
          isMenuOpen={isOpen}
          disabled={Boolean(disabled)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          {...getToggleButtonProps()}
        >
          <SelectItemStyled>
            {rowComponent ? (
              rowComponent(selectedItem)
            ) : (
              <Text
                margin="none"
                style={selectedItem ? {} : { color: theme.colors.secondary80w }}
              >
                {selectedItem ? itemToString(selectedItem) : placeholder}
              </Text>
            )}
            {isOpen ? (
              <IconChevron stroke={theme.colors.primary20b} direction="top" />
            ) : (
              <IconChevron
                stroke={theme.colors.primary20b}
                direction="bottom"
              />
            )}
          </SelectItemStyled>
        </SelectStyled>
        <MenuWrapper {...getMenuProps()}>
          {isOpen && (
            <MenuStyled>
              {items.map((item, index) => (
                <ItemStyled
                  highlighted={highlightedIndex === index}
                  bold={itemToString(item) === itemToString(selectedItem)}
                  key={`${itemToString(item)}${index}`}
                  {...getItemProps({ item, index })}
                >
                  {rowComponent ? (
                    rowComponent(item)
                  ) : (
                    <Text margin="none">{itemToString(item)}</Text>
                  )}
                </ItemStyled>
              ))}
            </MenuStyled>
          )}
        </MenuWrapper>
      </SelectWrapperStyled>
      {typeof error === 'string' && (
        <Text
          id={`${newId}-error`}
          style={{ color: theme.colors.danger, marginTop: SPACINGS[2] }}
          margin="none"
        >
          {error}
        </Text>
      )}
    </SelectContainer>
  );
}

export default React.memo(Select) as typeof Select;
